import { useRef, useEffect, useCallback } from 'react';

/**
 * useKaraokeSync - Optimized karaoke-style subtitle sync engine
 *
 * Improvements:
 * - Interpolated time using performance.now() for smoother sync
 * - Drift detection and resync every ~500ms if drift > 50ms
 * - Cursor-based segment lookup (O(1) amortized, no binary search)
 * - RAF pauses when audio paused or tab hidden
 * - Skip transition effect flag after seek
 */
export function useKaraokeSync({ audio, segments, isPlaying }) {
  const rafRef = useRef(null);
  const currentTimeRef = useRef(0);
  const cursorRef = useRef(0); // Cursor for forward-only traversal
  const activeSegmentRef = useRef(-1);
  const skipTransitionRef = useRef(false);
  const isSeekingRef = useRef(false);

  // Interpolation state: track audio time + performance.now() offset
  const audioTimeRef = useRef(0);
  const lastSyncTimeRef = useRef(0); // performance.now() when last synced with audio
  const lastResyncRef = useRef(0); // Last resync timestamp

  const onTimeUpdateRef = useRef(null);
  const onTimeUpdateContinuousRef = useRef(null);
  const onSegmentChangeRef = useRef(null);

  const setCallbacks = useCallback((timeUpdate, segmentChange, timeUpdateContinuous) => {
    onTimeUpdateRef.current = timeUpdate;
    onTimeUpdateContinuousRef.current = timeUpdateContinuous;
    onSegmentChangeRef.current = segmentChange;
  }, []);

  // Cursor-based segment lookup: move forward/backward based on time
  const findSegmentForTime = useCallback((time) => {
    if (!segments || segments.length === 0) return -1;

    const len = segments.length;
    let cursor = cursorRef.current;

    // Forward scan if time moved forward
    if (time >= segments[cursor]?.end) {
      while (cursor < len - 1 && time > segments[cursor].end) {
        cursor++;
      }
    } else if (time < segments[cursor]?.start) {
      while (cursor > 0 && time < segments[cursor].start) {
        cursor--;
      }
    }

    // Clamp cursor to valid range
    cursor = Math.max(0, Math.min(cursor, len - 1));

    // Check if cursor segment contains time
    const seg = segments[cursor];
    if (time >= seg.start && time <= seg.end) {
      cursorRef.current = cursor;
      return cursor;
    }

    // Fall back to linear search for edge cases
    for (let i = 0; i < len; i++) {
      if (time >= segments[i].start && time <= segments[i].end) {
        cursorRef.current = i;
        return i;
      }
    }

    return -1;
  }, [segments]);

  // Visibility check: is segment visible at time t?
  const isSegmentVisible = useCallback((seg, time, displayOffsetMs, pauseExtMs) => {
    const displayStart = seg.start - displayOffsetMs / 1000;
    const displayEnd = seg.end + pauseExtMs / 1000;
    return time >= displayStart && time <= displayEnd;
  }, []);

  // Sync loop
  const syncLoop = useCallback(() => {
    if (!audio || !isPlaying) return;

    const now = performance.now();

    // Resync with audio.currentTime every 500ms or if drift > 50ms
    const DRIFT_THRESHOLD_MS = 50;
    const RESYNC_INTERVAL_MS = 500;
    const needsResync =
      now - lastResyncRef.current > RESYNC_INTERVAL_MS ||
      Math.abs(audio.currentTime - audioTimeRef.current) * 1000 > DRIFT_THRESHOLD_MS;

    if (needsResync) {
      audioTimeRef.current = audio.currentTime;
      lastSyncTimeRef.current = now;
      lastResyncRef.current = now;
    }

    // Interpolate time using elapsed performance.now() delta
    const elapsed = (now - lastSyncTimeRef.current) / 1000;
    const interpolatedTime = audioTimeRef.current + elapsed;
    currentTimeRef.current = interpolatedTime;

    if (onTimeUpdateRef.current) {
      onTimeUpdateRef.current(interpolatedTime);
    }

    // Send continuous time for subtitle cursor (not floored)
    if (onTimeUpdateContinuousRef.current) {
      onTimeUpdateContinuousRef.current(interpolatedTime);
    }

    const segIdx = findSegmentForTime(interpolatedTime);

    if (segIdx !== activeSegmentRef.current) {
      activeSegmentRef.current = segIdx;

      if (onSegmentChangeRef.current) {
        onSegmentChangeRef.current(segIdx, skipTransitionRef.current);
      }

      // Clear skip flag after first segment change post-seek
      if (isSeekingRef.current) {
        isSeekingRef.current = false;
        skipTransitionRef.current = false;
      }
    }

    rafRef.current = requestAnimationFrame(syncLoop);
  }, [audio, isPlaying, findSegmentForTime]);

  // Pause RAF when audio paused or tab hidden
  useEffect(() => {
    if (!isPlaying || !audio) {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return;
    }

    rafRef.current = requestAnimationFrame(syncLoop);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isPlaying, audio, syncLoop]);

  // Pause RAF when tab hidden
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
      } else if (isPlaying && audio && !document.hidden) {
        // Re-sync on tab visible again
        if (audio.currentTime !== audioTimeRef.current) {
          audioTimeRef.current = audio.currentTime;
          lastSyncTimeRef.current = performance.now();
          lastResyncRef.current = performance.now();
        }
        rafRef.current = requestAnimationFrame(syncLoop);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isPlaying, audio, syncLoop]);

  // Seek: instant recompute, skip transition for 1 frame
  const seekTo = useCallback((time) => {
    if (!audio) return;
    currentTimeRef.current = time;
    audioTimeRef.current = time;
    lastSyncTimeRef.current = performance.now();
    lastResyncRef.current = performance.now();
    isSeekingRef.current = true;
    skipTransitionRef.current = true;

    const segIdx = findSegmentForTime(time);
    activeSegmentRef.current = segIdx;
  }, [audio, findSegmentForTime]);

  return {
    setCallbacks,
    seekTo,
    getCurrentTime: () => currentTimeRef.current,
    isSegmentVisible,
  };
}

export function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

export function getWordProgress(word, currentTime) {
  if (currentTime < word.start) return 0;
  if (currentTime >= word.end) return 1;
  return clamp((currentTime - word.start) / (word.end - word.start), 0, 1);
}

export function getWordState(word, currentTime) {
  if (currentTime < word.start) return 'future';
  if (currentTime >= word.end) return 'past';
  return 'active';
}
